import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import BookingInquiryModal from '@/components/BookingInquiryModal';
import AITalentManagerChat from '@/components/AITalentManagerChat';
import SkillStars from '@/components/SkillStars';
import { AvailabilityViewer } from '@/components/Calendar';
import { youtubeId, youtubeThumb } from '@/lib/video';
import { ARTIST_DETAIL } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Star, MapPin, BadgeCheck, Sparkles, Loader2, Calendar, Languages, IndianRupee, Briefcase,
  Trophy, Users, Clock, Plane, Play, Instagram, Youtube, Music2, Facebook, Twitter, X, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

import { FollowButton, ShareButton } from '@/components/FollowShare';

const formatFollowers = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}K`;
  return String(v);
};

const TRAVEL_LABEL = {
  city: 'City only',
  state: 'Within state',
  country: 'Across India',
  international: 'International (worldwide)',
};

const SOCIAL_META = {
  instagram: { label: 'Instagram', icon: Instagram, color: 'text-pink-400', followerLabel: 'Followers' },
  youtube: { label: 'YouTube', icon: Youtube, color: 'text-rose-400', followerLabel: 'Subscribers' },
  tiktok: { label: 'TikTok', icon: Music2, color: 'text-cyan-300', followerLabel: 'Followers' },
  facebook: { label: 'Facebook', icon: Facebook, color: 'text-blue-400', followerLabel: 'Followers' },
  x: { label: 'X / Twitter', icon: Twitter, color: 'text-slate-300', followerLabel: 'Followers' },
};

const ProfileHeader = ({ a, onBook }) => (
  <div className="flex flex-col md:flex-row gap-8 items-end mb-12">
    <div className="relative">
      <div className="h-44 w-44 rounded-3xl overflow-hidden border-4 border-[#0B1020] glass-strong">
        <img src={a.profile_image || a.cover_image} alt={a.stage_name} className="w-full h-full object-cover" />
      </div>
      {a.verified_level === 'elite' && (
        <div className="absolute -top-2 -right-2 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full p-2">
          <BadgeCheck className="h-5 w-5 text-white" />
        </div>
      )}
    </div>
    <div className="flex-1">
      <div className="flex flex-wrap gap-2 mb-3">
        {(a.categories || []).map((c) => (
          <span key={c} className="text-[10px] uppercase tracking-widest glass px-3 py-1 rounded-full text-slate-200">{c}</span>
        ))}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">{a.stage_name}</h1>
      <div className="flex flex-wrap items-center gap-5 mt-3 text-sm text-slate-300">
        <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {a.location_city}, {a.location_country}</span>
        <span className="flex items-center gap-1 text-amber-300"><Star className="h-4 w-4 fill-amber-300" /> {Number(a.rating_avg || 0).toFixed(1)} ({a.rating_count || 0} reviews)</span>
        <span className="flex items-center gap-1"><Briefcase className="h-4 w-4" /> {a.experience_years || 0}+ yrs</span>
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-3 md:flex-col md:items-end md:gap-3">
      <FollowButton
        artistId={a.artist_id}
        isFollowing={a.is_following}
        followerCount={a.follower_count}
      />
      <ShareButton artist={a} />
      <Button onClick={onBook} data-testid={ARTIST_DETAIL.bookButton} className="btn-rainbow text-white border-0 rounded-full px-7 py-6 text-base">
        <Sparkles className="h-4 w-4 mr-2" /> Book this artist
      </Button>
    </div>
  </div>
);

const SkillsSection = ({ a }) => {
  const ratings = a.skill_ratings || [];
  if (ratings.length === 0) return null;
  return (
    <div data-testid="artist-skills-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-300" /> Skills</h2>
      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
        {ratings.map((s, i) => (
          <div key={`${s.name}-${i}`} className="flex items-center justify-between gap-3 py-1">
            <span className="text-slate-200 text-sm capitalize">{s.name}</span>
            <SkillStars value={s.rating} size="md" />
          </div>
        ))}
      </div>
    </div>
  );
};

const PerformanceDetailsSection = ({ a }) => {
  const has = a.team_size || a.show_duration_min || a.travel_scope || (a.performance_languages || []).length;
  if (!has) return null;
  const items = [
    { icon: Users, label: 'Team size', value: a.team_size ? `${a.team_size} member${a.team_size > 1 ? 's' : ''} (incl. off-stage)` : null },
    { icon: Clock, label: 'Show duration', value: a.show_duration_min ? `${a.show_duration_min} minutes` : null },
    { icon: Plane, label: 'Travel scope', value: TRAVEL_LABEL[a.travel_scope] || null },
    { icon: Languages, label: 'Performance languages', value: (a.performance_languages || []).join(', ') || null },
  ].filter((i) => i.value);
  if (items.length === 0) return null;
  return (
    <div data-testid="artist-performance-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Briefcase className="h-5 w-5 text-cyan-300" /> Performance details</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-start gap-3">
            <Icon className="h-4 w-4 text-fuchsia-300 mt-1 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
              <div className="text-slate-200 text-sm">{value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MilestonesSection = ({ a }) => {
  const ms = a.milestones || [];
  if (ms.length === 0) return null;
  return (
    <div data-testid="artist-milestones-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /> Milestones &amp; achievements</h2>
      <div className="space-y-3">
        {ms.map((m, i) => (
          <div key={`${m.title}-${i}`} className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-4">
            {m.image_url ? (
              <img src={m.image_url} alt="" className="h-20 w-20 rounded-lg object-cover border border-white/10 shrink-0" />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-400/20 flex items-center justify-center shrink-0">
                <Trophy className="h-7 w-7 text-amber-300" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-3 flex-wrap">
                <h3 className="text-white font-medium">{m.title}</h3>
                {m.year && <span className="text-[10px] uppercase tracking-widest text-amber-300">{m.year}</span>}
              </div>
              {m.description && <p className="text-slate-300 text-sm mt-1 leading-relaxed">{m.description}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const VideoThumbCard = ({ url, onPlay, testId }) => {
  const id = youtubeId(url);
  const thumb = youtubeThumb(url);
  if (!id) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="aspect-video glass rounded-xl border border-white/10 flex items-center justify-center text-cyan-300 text-sm hover:border-cyan-400/40 transition">
        <Play className="h-5 w-5 mr-2" /> Open
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onPlay(id)}
      data-testid={testId}
      className="group relative aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-fuchsia-400/40 transition"
    >
      <img src={thumb} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
        <div className="h-14 w-14 rounded-full btn-rainbow flex items-center justify-center shadow-2xl">
          <Play className="h-6 w-6 text-white ml-1 fill-white" />
        </div>
      </div>
    </button>
  );
};

const PastPerformanceSection = ({ a, onPlay }) => {
  const urls = (a.embed_urls || []).filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <div data-testid="artist-past-performance-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Youtube className="h-5 w-5 text-rose-400" /> Past performances</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {urls.slice(0, 8).map((url, i) => (
          <VideoThumbCard key={`${url}-${i}`} url={url} onPlay={onPlay} testId={`artist-past-video-${i}`} />
        ))}
      </div>
    </div>
  );
};

const TestimonialsSection = ({ a, onPlay }) => {
  const urls = (a.testimonial_video_urls || []).filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <div data-testid="artist-testimonials-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-300" /> Testimonials</h2>
      <p className="text-xs text-slate-400 mb-4">Real words from past clients.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {urls.map((url, i) => (
          <VideoThumbCard key={`${url}-${i}`} url={url} onPlay={onPlay} testId={`artist-testimonial-video-${i}`} />
        ))}
      </div>
    </div>
  );
};

const SocialProofSection = ({ a }) => {
  const followers = a.social_followers || {};
  const verified = a.social_verified || {};
  const shown = Object.entries(followers).filter(([, n]) => Number(n) > 0);
  if (shown.length === 0) return null;
  return (
    <div data-testid="artist-social-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-1">Social presence</h2>
      <p className="text-xs text-slate-400 mb-5">Self-reported audience across platforms (display only — links are not shareable here). A green check means our team verified the count.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {shown.map(([key, n]) => {
          const meta = SOCIAL_META[key];
          if (!meta) return null;
          const Icon = meta.icon;
          const isVerified = verified[key] === 'verified';
          return (
            <div key={key} className="glass-strong rounded-2xl border border-white/10 p-4 text-center relative" data-testid={`artist-social-${key}`}>
              {isVerified && (
                <span data-testid={`artist-social-verified-${key}`} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-emerald-300 font-medium bg-emerald-500/10 border border-emerald-400/30 rounded-full px-1.5 py-0.5">
                  <CheckCircle2 className="h-2.5 w-2.5" />
                </span>
              )}
              <Icon className={`h-7 w-7 ${meta.color} mx-auto mb-2`} />
              <div className="font-display text-2xl font-bold text-white">{formatFollowers(n)}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{meta.followerLabel}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{meta.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GallerySection = ({ a }) => {
  const imgs = a.portfolio_images || [];
  if (imgs.length === 0) return null;
  return (
    <div data-testid="artist-gallery-section" className="glass rounded-2xl p-7 border border-white/10">
      <h2 className="font-display text-xl text-white mb-4">Gallery</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {imgs.map((src) => (
          <div key={src} className="aspect-square rounded-xl overflow-hidden border border-white/10">
            <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition" />
          </div>
        ))}
      </div>
    </div>
  );
};

const VideoLightbox = ({ videoId, onClose }) => {
  if (!videoId) return null;
  return (
    <div
      data-testid="artist-video-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in"
    >
      <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title="Performance video"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="w-full h-full rounded-2xl border border-white/10 shadow-2xl"
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="artist-video-lightbox-close"
          className="absolute -top-4 -right-4 h-10 w-10 rounded-full bg-white text-black flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const ReviewsSection = ({ artistId, reviews, ratingAvg, ratingCount, onPosted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState('5');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please sign in to leave a review'); return; }
    setPosting(true);
    try {
      await api.post('/reviews', { artist_id: artistId, rating: parseInt(rating), comment });
      toast.success('Review posted!');
      setComment(''); setRating('5'); setShowForm(false);
      onPosted();
    } catch (err) {
      toast.error('Could not post review', { description: err?.response?.data?.detail });
    } finally { setPosting(false); }
  };

  const avg = Number(ratingAvg || 0);
  const count = Number(ratingCount || reviews.length || 0);
  // Distribution (1..5) for the mini-bar chart
  const dist = [5, 4, 3, 2, 1].map((s) => ({
    star: s,
    pct: count > 0 ? Math.round((reviews.filter((r) => r.rating === s).length / count) * 100) : 0,
  }));

  return (
    <div data-testid="artist-reviews-section" className="glass rounded-2xl p-7 border border-white/10">
      {/* Summary header */}
      <div className="flex items-start gap-6 flex-wrap">
        <div className="text-center">
          <div className="font-display text-5xl font-bold text-white leading-none">{avg.toFixed(1)}</div>
          <div className="flex items-center gap-0.5 mt-2 justify-center">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className={`h-4 w-4 ${i <= Math.round(avg) ? 'fill-amber-300 text-amber-300' : 'text-slate-600'}`} />
            ))}
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            {count} review{count === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex-1 min-w-[200px] space-y-1.5">
          {dist.map(({ star, pct }) => (
            <div key={star} className="flex items-center gap-2 text-xs text-slate-400">
              <span className="w-3 text-right text-slate-500">{star}</span>
              <Star className="h-3 w-3 fill-amber-300/60 text-amber-300/60 shrink-0" />
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="w-8 text-right text-slate-500">{pct}%</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2 self-center">
          {count > 0 && (
            <Button
              type="button"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              data-testid="artist-reviews-toggle"
              variant="outline"
              className="border-white/10 bg-transparent text-slate-200 hover:bg-white/5 rounded-full text-xs"
            >
              {expanded ? 'Hide reviews' : `Read all ${count}`}
            </Button>
          )}
          {user && (
            <Button
              type="button"
              size="sm"
              onClick={() => setShowForm((v) => !v)}
              data-testid="artist-reviews-write"
              className="btn-ai text-white border-0 rounded-full text-xs"
            >
              Write review
            </Button>
          )}
        </div>
      </div>

      {/* Expandable reviews list */}
      {expanded && reviews.length > 0 && (
        <div data-testid="artist-reviews-list" className="space-y-4 mt-6 pt-6 border-t border-white/5">
          {reviews.map((r) => (
            <div key={r.review_id} className="border-b border-white/5 pb-4 last:border-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-white font-medium">{r.client_name || 'Client'}</span>
                <span className="flex items-center gap-0.5 text-amber-300 text-xs">
                  {Array.from({ length: r.rating }).map((_, i) => <Star key={`${r.review_id}-star-${i}`} className="h-3 w-3 fill-amber-300" />)}
                </span>
              </div>
              <p className="text-slate-300 text-sm">{r.comment}</p>
            </div>
          ))}
        </div>
      )}

      {count === 0 && (
        <p className="text-slate-400 text-sm mt-6 pt-6 border-t border-white/5">Be the first to review this artist.</p>
      )}

      {/* Inline write form (collapsible) */}
      {user && showForm && (
        <form onSubmit={submitReview} className="mt-6 pt-6 border-t border-white/5 space-y-3">
          <div className="flex items-center gap-3">
            <Select value={rating} onValueChange={setRating}>
              <SelectTrigger data-testid={ARTIST_DETAIL.ratingSelect} className="w-32 bg-white/5 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-strong border-white/10 text-slate-200">
                {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} stars</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Textarea data-testid={ARTIST_DETAIL.reviewInput} value={comment} onChange={(e) => setComment(e.target.value)} required placeholder="Share your experience…" className="bg-white/5 border-white/10" />
          <Button type="submit" data-testid={ARTIST_DETAIL.reviewSubmit} disabled={posting} className="btn-ai text-white border-0 rounded-full px-6">
            {posting ? 'Posting…' : 'Post review'}
          </Button>
        </form>
      )}
    </div>
  );
};

const InfoSidebar = ({ a }) => (
  <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
    <div className="glass-strong rounded-2xl p-6 border border-white/10">
      <div className="text-xs uppercase tracking-widest text-fuchsia-300 mb-1">Pricing range</div>
      <div className="flex items-center gap-2 text-white">
        <IndianRupee className="h-5 w-5" />
        <span className="font-display text-2xl font-bold">{Number(a.price_min || 0).toLocaleString('en-IN')}</span>
        <span className="text-slate-400">–</span>
        <span className="font-display text-2xl font-bold">{Number(a.price_max || 0).toLocaleString('en-IN')}</span>
      </div>
      <p className="text-xs text-slate-500 mt-2">Final price depends on event type, location &amp; duration.</p>
    </div>

    <div data-testid="artist-availability-section" className="glass rounded-2xl p-5 border border-white/10">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="h-4 w-4 text-emerald-300" />
        <h2 className="font-display text-base text-white">Availability</h2>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Next 90 days</p>
      <AvailabilityViewer artistId={a.artist_id} />
    </div>

    <div className="glass rounded-2xl p-5 border border-white/10 space-y-3">
      <div className="flex items-start gap-3">
        <Languages className="h-4 w-4 text-cyan-300 mt-1" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Languages</div>
          <div className="text-slate-200 text-sm">{(a.languages || []).join(', ') || '—'}</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Sparkles className="h-4 w-4 text-fuchsia-300 mt-1" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Skills</div>
          <div className="text-slate-200 text-sm">{(a.skills || []).join(', ') || '—'}</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Calendar className="h-4 w-4 text-emerald-300 mt-1" />
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Reply time</div>
          <div className="text-slate-200 text-sm">Within 24 hours</div>
        </div>
      </div>
    </div>

    {a.match_score && (
      <div className="glass-strong rounded-2xl p-6 border border-emerald-500/30 text-center">
        <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">AI Match Score</div>
        <div className="font-display text-5xl font-bold ai-text">{a.match_score}%</div>
      </div>
    )}
  </aside>
);

const ArtistDetail = () => {
  const { id, username } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  const load = useCallback(async () => {
    const path = username ? `/u/${username}` : `/artists/${id}`;
    const { data: res } = await api.get(path);
    setData(res);
  }, [id, username]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;
  }

  const a = data.artist;

  return (
    <div data-testid={ARTIST_DETAIL.root} className="pb-24">
      {/* Cover */}
      <div className="relative h-[55vh] min-h-[400px] w-full overflow-hidden">
        <img src={a.cover_image || a.profile_image} alt={a.stage_name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1020] via-[#0B1020]/60 to-[#0B1020]/30" />
      </div>

      <div className="max-w-6xl mx-auto px-6 sm:px-12 -mt-32 relative z-10">
        <ProfileHeader a={a} onBook={() => setOpen(true)} />

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="glass rounded-2xl p-7 border border-white/10">
              <h2 className="font-display text-xl text-white mb-3">About</h2>
              <p className="text-slate-300 leading-relaxed">{a.ai_bio || a.bio || 'This artist is just getting started on Maarivil.'}</p>
            </div>

            <SkillsSection a={a} />
            <PerformanceDetailsSection a={a} />
            <MilestonesSection a={a} />
            <PastPerformanceSection a={a} onPlay={setActiveVideo} />
            <GallerySection a={a} />
            <TestimonialsSection a={a} onPlay={setActiveVideo} />
            <SocialProofSection a={a} />
            <ReviewsSection
              artistId={a.artist_id}
              reviews={data.reviews}
              ratingAvg={a.rating_avg}
              ratingCount={a.rating_count || data.reviews.length}
              onPosted={load}
            />
          </div>

          <InfoSidebar a={a} />
        </div>
      </div>

      <BookingInquiryModal artist={a} open={open} onOpenChange={setOpen} />
      <AITalentManagerChat artist={a} />
      <VideoLightbox videoId={activeVideo} onClose={() => setActiveVideo(null)} />
    </div>
  );
};

export default ArtistDetail;
